import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import classNames from 'classnames';
import { ChangeEvent, ReactElement, ReactNode, Ref } from 'react';

import { IOption } from '~/types';

import styles from './SelectMUI.module.scss';

export interface ISelectMUIProps {
  value: IOption['value'] | '' | null;
  onChange: (value: IOption['value']) => void;
  options: IOption[];
  label: string;
  placeholder?: string;
  loading?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: ReactNode;
  className?: string;
  renderOption?: (option: IOption) => ReactNode;
  name?: string;
  onBlur?: () => void;
  inputRef?: Ref<HTMLInputElement>;
}

const SelectMUI = ({
  value,
  onChange,
  options,
  label,
  placeholder = '',
  loading = false,
  disabled = false,
  error = false,
  helperText,
  className,
  renderOption = option => option.label,
  name,
  onBlur,
  inputRef,
}: ISelectMUIProps): ReactElement => {
  const renderValue = (selected: unknown): ReactNode => {
    const option = options.find(item => item.value === selected);
    if (option) return renderOption(option);
    return <span className={styles.placeholder}>{loading ? 'Loading…' : placeholder}</span>;
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const option = options.find(item => String(item.value) === String(event.target.value));
    if (option) onChange(option.value);
  };

  return (
    <TextField
      select
      className={classNames(styles.root, className)}
      label={label}
      size="small"
      fullWidth
      name={name}
      value={value ?? ''}
      onChange={handleChange}
      onBlur={onBlur}
      inputRef={inputRef}
      disabled={disabled}
      error={error}
      helperText={helperText}
      slotProps={{
        inputLabel: { shrink: true },
        select: {
          displayEmpty: true,
          renderValue,
          MenuProps: { slotProps: { paper: { className: styles.paper } } },
        },
      }}
    >
      {options.map(option => (
        <MenuItem key={option.value} value={option.value} disabled={option.isDisabled}>
          {renderOption(option)}
        </MenuItem>
      ))}
    </TextField>
  );
};

export default SelectMUI;
